import Context from "../app.context";
import { Store } from "../lib/redux";
import { Dialog, applyDriverActionNecessary, removeDialog } from "./framework/dialog";
import { setCurrentVehicle, getCurrentVehicle } from "../utils/device";
import { Source, log, error as logError } from "../utils/logger";
import { DutyStatusLogService } from "../model/DutyStatusLogs";
import { getServerDate } from "../utils/storage/date";
import { BaseCache, makeDeviceCache } from "../utils/storage/cache";
import { dispatcher, DispatcherEvents } from "../utils/dispatcher";
import { isDriverViewingInspectionMode, isOnLogin, isDriverOnAnyWorkflow, isOnSwitchUserLogin } from "../utils/uri";
import { getMobileLocation } from "../utils/location";
import { CancelablePromise as Promise } from "../utils/cancelablePromise";
import { Router } from "../router";
import { MobileActions } from "../store/mobile/actions";
import { DataActions } from "../store/data/actions";
import { ActiveEventType, IVehicleSelectedPayload } from "../store/data/types";
import { Device } from "../model/Device";
import DriverChangeLogs from "../model/DriverChangeLogs";
import { htmlEscape } from "../utils/vanilla";
import type { THosStatus } from "hos-tools";
import { noteLogoutRace } from "../utils/logoutRaceDiagnostics";
import type DutyStatusLog from "../model/DutyStatusLog";

export class VehicleEvents {
    private readonly store: Store;
    private readonly router: Router;
    private context: Context;
    private readonly deviceCache: BaseCache<Device | IDevice>;
    private isProcessingVehicleRemoved = false;
    private syncReplayEvent: DispatcherEvents | null = null;
    private readonly pendingVehicleSelection: DeepReadonly<IVehicleSelectedPayload> | undefined;
    private isProcessingVehicleSelection = false;

    constructor(store: Store, context: Context, router: Router, private readonly dutyStatusLogService: DutyStatusLogService) {
        this.store = store;
        this.router = router;
        this.deviceCache = makeDeviceCache(this.store);
        // Only replay work restored at startup; a live selection still owns its normal continuation.
        this.pendingVehicleSelection = this.store.getState().data.activeEvents?.[ActiveEventType.VehicleSelected];
        this.setContext(context);
    }

    setContext(context: Context) {
        this.context = context;
        if (this.syncReplayEvent) {
            this.updateSyncReplayListener();
        }
    }

    startWatching(): VehicleEvents {
        // eslint-disable-next-line @typescript-eslint/unbound-method
        dispatcher.on(DispatcherEvents.VehicleRemoved, (assignedVehicle?: IIdEntity, overrideDate?: Date) => {
            let hash = this.store.getState().mobile.currentPage;
            log(Source.USER_EVENT, `Vehicle being deselected. hash: ${ hash } `
                + `context: ${ this.context.user } assignedVehicle: ${ assignedVehicle ? assignedVehicle.id : "n/a" } stack: ${ new Error("").stack }`);
            return this.assignDriverToVehicle(assignedVehicle, overrideDate);
        }, this);

        this.updateSyncReplayListener();
        this.replayPendingVehicleSelection();
        return this;
    }

    stopWatching(): VehicleEvents {
        // eslint-disable-next-line @typescript-eslint/unbound-method
        dispatcher.off(DispatcherEvents.VehicleRemoved, undefined, this);
        if (this.syncReplayEvent) {
            dispatcher.off(this.syncReplayEvent, undefined, this);
            this.syncReplayEvent = null;
        }
        return this;
    }

    replayPendingEvents(): void {
        const activeEvent = this.store.getState().data.activeEvents?.[ActiveEventType.VehicleRemoved];

        // MYG-147182 (logging only): record a persisted VehicleRemoved being replayed by the sync listener while logout is in progress.
        if (activeEvent) {
            noteLogoutRace("vehicleRemovedReplayed", () => ({
                trigger: this.syncReplayEvent,
                assignedVehicleId: activeEvent.assignedVehicleId,
                driverChangeDateTime: activeEvent.driverChangeDateTime,
                isProcessingVehicleRemoved: this.isProcessingVehicleRemoved,
                contextUser: this.context.user
            }));
        }

        // Nothing pending, or dialog is already being shown
        if (!activeEvent || this.isProcessingVehicleRemoved) {
            return;
        }

        const activeUserIds = this.context.userService.getUsers(true).map(user => user.id);
        const lastDriverChange = new DriverChangeLogs(this.context.api, this.store)
            .getLastDriverChangeForAllDrivers(activeUserIds)[0] ?? null;

        const diagnosticUser = this.context.userService.getDriversSeatUser() || this.context.userService.getCurrentUser();
        const logReplayDecision = (decision: string, details?: Record<string, unknown>) => {
            log(Source.USER_EVENT, `[VehicleDissociation] ${ JSON.stringify({
                decision,
                assignedVehicleId: activeEvent.assignedVehicleId,
                driverChangeDateTime: activeEvent.driverChangeDateTime ?? null,
                driverChangeId: lastDriverChange?.id ?? null,
                driverChangeDriverId: lastDriverChange?.driver.id ?? null,
                driverChangeDeviceId: lastDriverChange?.device.id ?? null,
                ...details
            }) }`, diagnosticUser);
        };

        const clearEvent = () => this.store.dispatch(DataActions.removeActiveEvent(ActiveEventType.VehicleRemoved));

        // No driver change, cannot process. Clear and exit.
        if (!lastDriverChange) {
            logReplayDecision("discardMissingDriverChange");
            clearEvent();
            return;
        }

        // If the current state of assignment has changed, the event is stale. Clear the event and exit.
        const deviceIdByDriverChange = activeUserIds.indexOf(lastDriverChange.driver.id) > -1 ? lastDriverChange.device.id : "NoDeviceId";
        if (deviceIdByDriverChange !== activeEvent.assignedVehicleId) {
            logReplayDecision("discardStaleAssignment", {
                deviceIdByDriverChange
            });
            clearEvent();
            return;
        }

        const assignedVehicle: IIdEntity | undefined = activeEvent.assignedVehicleId !== "NoDeviceId"
            ? { id: activeEvent.assignedVehicleId }
            : undefined;

        const overrideDate = activeEvent.driverChangeDateTime ? new Date(activeEvent.driverChangeDateTime) : undefined;

        // Don't force open the dialog if the current page blocks it; the persisted event retries after next sync
        logReplayDecision("replayAttempt");
        void this.assignDriverToVehicle(assignedVehicle, overrideDate);
    }

    /**
     * Fired as a result of `DispatcherEvents.VehicleRemoved` where a vehicle is removed
     * from database, or not set within the app. NO DRIVERCHANGE IS ADDED/SET IN THIS METHOD
     * Steps:
     * - For each active user, if approproiate create HOS ON/OFF log with annotation about this
     * - Set current vehicle to NoDeviceId
     * - Run AppManager.onVehicleSelected which is set to apply creating Login/Logoff logs for each active user.
     *      Logoff from previous vehicle and a Login for NoDeviceId
     * - Trigger DispatchEvents.VehicleSelected w/ `NoDeviceId`, `oldVehicle`
     *      This informs rest of app to re-render / re-establish server events
     * - Dialog is triggered that informs user they were "disassociated from the vehicle"
     * @param assignedVehicle New vehicle that the driver was assigned to
     * @returns A new Dialog that will inform the user their vehicle has been changed
     */
    assignDriverToVehicle(assignedVehicle?: IIdEntity, overrideDate?: Date): Promise<Dialog | undefined> {
        // Remove the user stopped/ignition cycle dialog if this one appears. They must have ended their status if they were dissociated.
        removeDialog("user-log-prompt-dialog");

        // if this dialog is already open then don't open
        if (document.getElementById("vehicle-removed-dialog")) {
            return Promise.resolve();
        }

        // MYG-147182 (logging only): record VehicleRemoved being persisted as an active event while logout is in progress.
        noteLogoutRace("vehicleRemovedPersisted", () => ({
            assignedVehicleId: assignedVehicle?.id ?? "NoDeviceId",
            driverChangeDateTime: overrideDate?.toISOString(),
            isOnLogin: isOnLogin(),
            isProcessingVehicleRemoved: this.isProcessingVehicleRemoved,
            contextUser: this.context.user
        }));

        // Always overwrite so a newer driver change (different assigned vehicle) is reflected in the stored event.
        this.store.dispatch(DataActions.addActiveEvent({
            [ActiveEventType.VehicleRemoved]: {
                assignedVehicleId: assignedVehicle?.id ?? "NoDeviceId",
                driverChangeDateTime: overrideDate?.toISOString()
            }
        }));

        if (this.shouldBlockDialog()) {
            return Promise.resolve();
        }
        if (this.isProcessingVehicleRemoved) {
            return Promise.resolve();
        }
        this.isProcessingVehicleRemoved = true;

        const api = this.context.api;
        let currentUser = this.context.userService.getCurrentUser();
        let currentDriver = this.context.userService.getDriversSeatUser();
        let isCurrentUserDriver = currentUser && currentDriver && currentUser.id === currentDriver?.id;
        let activeUsers = this.context.userService.getUsers(true);

        let oldVehicle = getCurrentVehicle() || undefined;
        let newVehicle = assignedVehicle || { id: "NoDeviceId" };
        // True when replaying after a refresh that happened after setCurrentVehicle — skip state changes, only show dialog.
        const isVehicleAlreadySwitched = !!oldVehicle && oldVehicle.id === newVehicle.id;
        let newVehiclePopulated: INamedEntity = { id: assignedVehicle?.id || "NoDeviceId", name: "" };

        log(Source.USER_EVENT, `[VehicleDissociation] ${ JSON.stringify({
            decision: isVehicleAlreadySwitched ? "alreadySwitched" : "assignmentStarted",
            oldVehicleId: oldVehicle?.id ?? null,
            targetVehicleId: newVehicle.id,
            overrideDatePresent: overrideDate !== undefined,
            overrideDate: overrideDate?.toJSON() ?? null
        }) }`, currentDriver || currentUser);

        const resetProcessing = (): Promise<Dialog | undefined> => {
            this.isProcessingVehicleRemoved = false;
            return Promise.resolve(undefined);
        };

        let triggerDialog = (): Promise<Dialog> => {
            dispatcher.trigger(DispatcherEvents.VehicleSelected, newVehicle, oldVehicle);
            // Sync if the vehicle was changed
            !isVehicleAlreadySwitched && assignedVehicle && this.context.sync.synchronize();
            let disassociatedDriver: DeepReadonly<IUser> | null = currentUser && currentDriver && !isCurrentUserDriver ? currentDriver : null;
            return this.vehicleDissociated(disassociatedDriver, assignedVehicle ? newVehiclePopulated : undefined);
        };

        if (isVehicleAlreadySwitched) {
            return triggerDialog().catch(resetProcessing);
        }

        return Promise.all([
            this.context.translationService.getTranslator().translate(assignedVehicle ? "The driver was assigned to a new vehicle." : "The driver was dissociated from the vehicle."),
            getMobileLocation(oldVehicle),
            (assignedVehicle ? this.deviceCache.getOrFetch(api, [assignedVehicle], api.getUser()?.id) : Promise.resolve([]))
        ])
            .then(([translatedMessage, logLocation, deviceArray]) => {
                newVehiclePopulated = deviceArray.length ? deviceArray[0][0] : newVehiclePopulated;
                activeUsers.forEach(user => {
                    let canAccessHOS = this.context.securityService.can("useHos", user.id);
                    let jurisdiction = this.store.getState().data.ELDJurisdiction;
                    if (canAccessHOS && jurisdiction !== "EWD") {
                        let standardStatusLogs = this.dutyStatusLogService.getSortedHosLogs(this.dutyStatusLogService.getHOSLogs(false, user.id)
                            .filter(hosLog => hosLog.isStandardStatusDriverLog()));
                        let latestLog = this.dutyStatusLogService.getLatestLog(standardStatusLogs);
                        const logStatusDecision = (decision: string, newStatusLog?: DutyStatusLog) => {
                            log(Source.USER_EVENT, `[VehicleDissociation] ${ JSON.stringify({
                                decision,
                                userId: user.id,
                                assignedVehicleId: assignedVehicle?.id ?? "NoDeviceId",
                                oldVehicleId: oldVehicle?.id ?? null,
                                overrideDatePresent: overrideDate !== undefined,
                                overrideDate: overrideDate?.toJSON() ?? null,
                                cachedStandardLogCount: standardStatusLogs.length,
                                cachedLogId: latestLog?.id ?? null,
                                cachedLogStatus: latestLog?.status ?? null,
                                cachedLogDateTime: latestLog?.dateTime ?? null,
                                cachedLogDeviceId: latestLog?.getDeviceId() ?? null,
                                newStatusLogId: newStatusLog?.id ?? null,
                                newStatus: newStatusLog?.status ?? null,
                                newStatusLogDateTime: newStatusLog?.dateTime ?? null,
                                newStatusLogDeviceId: newStatusLog?.getDeviceId() ?? null,
                                newStatusOrigin: newStatusLog?.origin ?? null
                            }) }`, user);
                        };

                        if (latestLog && user) {
                            let latestLogStatus: THosStatus = latestLog.status;
                            let isSituationalDriving = latestLog.isSituationalDrivingStatus();
                            if (!(latestLog.getDeviceId() === "NoDeviceId" && !isSituationalDriving)) {
                                let newStatus: THosStatus = (latestLogStatus === "YM" || latestLogStatus === "ON" || latestLogStatus === "D") ? "ON" : "OFF";
                                let logDate = new Date(latestLog.dateTime);
                                let manualLogDate = getServerDate();
                                let beforeManualLogDate = new Date(manualLogDate.getTime() - 1000);

                                // rare edge case where the log may be created instantaneously. Need to make sure it's not made in the wrong spot... and not within the same ms
                                let newLogDate = beforeManualLogDate.getTime() - 1000 < logDate.getTime() ? new Date(logDate.getTime() + 1000) : beforeManualLogDate;

                                // Sync all logs 'later', because a sync will also occur for login/logoff logs.
                                if (isSituationalDriving) {
                                    this.dutyStatusLogService.getLogsToEndDrivingExemption(user as IUser, latestLog, newLogDate, "OtherUser")
                                        .forEach(newLog => dispatcher.trigger(DispatcherEvents.UserAddDSLForDriver, newLog, true));
                                }

                                let newStatusLog = this.dutyStatusLogService.createManualDutyStatusLog(
                                    new Date(newLogDate.getTime() + 1000).toISOString(),
                                    newVehicle,
                                    user,
                                    this.context.userService.getCoDrivers(user.name),
                                    newStatus,
                                    "OtherUser",
                                    translatedMessage,
                                    logLocation || undefined
                                );
                                logStatusDecision("createdStatusLog", newStatusLog);
                                dispatcher.trigger(DispatcherEvents.UserAddDSLForDriver, newStatusLog, true);
                            } else {
                                logStatusDecision("skipAlreadyUnassigned");
                            }
                        } else {
                            logStatusDecision("noCachedStandardLog");
                        }
                    }
                    this.deviceCache.add([newVehiclePopulated], user.id);
                });
                setCurrentVehicle(newVehicle);

                let loggerMessage = assignedVehicle
                    ? `Driver was changed to a new vehicle (${ assignedVehicle.id }), switching automatically`
                    : "Driver was dissociated, switching to NoVehicle automatically";
                log(Source.ENTITY, loggerMessage, this.context.userService.getCurrentUser());
            })
            .then(() => this.context.manager.onVehicleSelected(this.context, newVehicle, oldVehicle, !!assignedVehicle, overrideDate))
            .then(triggerDialog)
            .catch(() => {
                logError(Source.ERROR, "Encountered an error creating login/logoff logs.", this.context.userService.getCurrentUser());
                return triggerDialog().catch(resetProcessing);
            });
    }

    private shouldBlockDialog(): boolean {
        // Allow the dialog on switch-user login (co-driver reselection)
        if (isOnSwitchUserLogin()) {
            return false;
        }
        return !location.hash
            || isDriverOnAnyWorkflow()
            || isOnLogin()
            || isDriverViewingInspectionMode();
    }

    private getPendingVehicleSelection(): DeepReadonly<IVehicleSelectedPayload> | undefined {
        const restored = this.pendingVehicleSelection;
        const pending = this.store.getState().data.activeEvents?.[ActiveEventType.VehicleSelected];
        if (!restored || !pending) {
            return undefined;
        }
        const isSameOperation = restored.operationId || pending.operationId
            ? restored.operationId === pending.operationId
            : restored.userId === pending.userId && restored.deviceId === pending.deviceId && restored.dateTime === pending.dateTime;
        return isSameOperation ? pending : undefined;
    }

    private replayPendingVehicleSelection(userId?: string): void {
        const pending = this.getPendingVehicleSelection();
        if (!pending) {
            return;
        }
        if (pending.pendingUserIds && pending.pendingUserIds.indexOf(pending.userId) === -1) {
            this.replayCoDriverSelectionLogs(pending);
            this.store.dispatch(DataActions.removeActiveEvent(ActiveEventType.VehicleSelected));
            return;
        }
        if (userId !== pending.userId) {
            if (userId) {
                this.replayCoDriverSelectionLogs(pending, userId);
            }
            return;
        }
        if (this.isProcessingVehicleSelection) {
            return;
        }
        const user = this.context.userService.getUserById(userId);
        const context = this.context.api.getUser()?.id === userId ? this.context : user && this.context.manager.getCachedContext(user.name);
        if (!user || !context?.api.getCurrentCredentials() || context.api.getUser()?.id !== userId) {
            return;
        }

        this.replayCoDriverSelectionLogs(pending);
        this.isProcessingVehicleSelection = true;
        void Promise.resolve()
            .then(() => Promise.all(context.vehicleService.createLoginLogoffLogs(user, { id: pending.deviceId }, new Date(pending.dateTime), true)))
            .then(() => {
                // Co-driver recovery is best effort and must not keep the selecting driver's marker alive.
                if (this.getPendingVehicleSelection()) {
                    this.store.dispatch(DataActions.removeActiveEvent(ActiveEventType.VehicleSelected));
                }
            })
            .catch(() => {
                logError(Source.ERROR, "Encountered an error creating login/logoff logs.", user);
            })
            .then(() => {
                this.isProcessingVehicleSelection = false;
            });
    }

    private replayCoDriverSelectionLogs(pending: DeepReadonly<IVehicleSelectedPayload>, syncedUserId?: string): void {
        for (const userId of pending.pendingUserIds || []) {
            if (userId === pending.userId || (syncedUserId && userId !== syncedUserId)) {
                continue;
            }
            const user = this.context.userService.getUserById(userId);
            const context = this.context.api.getUser()?.id === userId ? this.context : user && this.context.manager.getCachedContext(user.name);
            if (context?.api.getUser()?.id === userId) {
                this.context.manager.tryCreateCoDriverLogs(context, { id: pending.deviceId }, new Date(pending.dateTime), {
                    allowEmptyHistory: userId === syncedUserId
                });
            }
        }
    }

    private updateSyncReplayListener() {
        if (this.syncReplayEvent) {
            dispatcher.off(this.syncReplayEvent, undefined, this);
        }
        const anyUserHasAttemptHos = this.context.userService.getUsers(true)
            .some(user => this.context.securityService.can("attemptHos", user.id));
        this.syncReplayEvent = anyUserHasAttemptHos ? DispatcherEvents.SyncHos : DispatcherEvents.SyncDriverChange;
        dispatcher.on(this.syncReplayEvent, (userId?: string) => {
            this.replayPendingEvents();
            if (this.syncReplayEvent === DispatcherEvents.SyncHos) {
                this.replayPendingVehicleSelection(userId);
            }
        }, this);
    }

    /**
     * Shows prompt when user is dissociated from current vehicle or assigned to a new one.
     * Will automatically switch the user to the other vehicle / no vehicle, but driver will be given the option of changing their vehicle.
     * @param driver The driver will only be populated if the current user is not the driver
     * @param assignedVehicle New vehicle that the driver was assigned to
     * @returns A new Dialog that will inform the user their vehicle has been changed
     */
    private vehicleDissociated(driver: DeepReadonly<IUser> | null, assignedVehicle?: INamedEntity): Promise<Dialog> {
        let message;
        let newVehicleName = assignedVehicle ? htmlEscape(assignedVehicle.name || "") : "";

        if (assignedVehicle) {
            let vehicleNameString = newVehicleName ? `<br><span>Vehicle Name</span><span class="noTranslate">: ${ newVehicleName }</span>` : "";
            if (driver) {
                message = "The driver was assigned to a new vehicle. "
                    + "If you select another vehicle, you will be switched to the driver's view. Do you want to select another vehicle?"
                    + vehicleNameString;
            } else {
                message = `You have been assigned to a new vehicle. Would you like to select another vehicle?`
                    + vehicleNameString;
            }
        } else {
            if (driver) {
                message = "The driver was unassigned from this vehicle. "
                    + "If you select another vehicle, you will be switched to the driver's view. Do you want to select another vehicle?";
            } else {
                message = "You are no longer assigned to this vehicle. It may have been claimed by another driver. Would you like to select another vehicle?";
            }
        }

        const dismissDialog = (dialog: Dialog) => {
            this.isProcessingVehicleRemoved = false;
            this.store.dispatch(DataActions.removeActiveEvent(ActiveEventType.VehicleRemoved));
            dialog.remove();
        };

        const vehicleRemovedDialog = new Dialog(
            "vehicle-removed-dialog",
            message,
            this.context.translationService.getTranslator(),
            "Confirmation Required",
            [[
                {
                    id: "stay-no-vehicle",
                    className: "button_secondary",
                    text: "No",
                    callback: () => dismissDialog(vehicleRemovedDialog)
                }, {
                    id: "select-new-vehicle",
                    text: "Yes",
                    callback: () => {
                        dismissDialog(vehicleRemovedDialog);
                        this.store.dispatch(MobileActions.setPreviousPage(""));

                        if (!driver) {
                            this.router.toWorkflow(false, { changeVehicle: true });
                        } else {
                            this.router.navigateToPage("login", {
                                switchUser: driver.id,
                                reselectVehicle: true
                            });
                        }
                    }
                }
            ]],
            {
                closable: false,
                isNightMode: this.store.getState().data.nightMode
            }
        );

        return vehicleRemovedDialog.open()
            .then(applyDriverActionNecessary(this.store, window.webViewLayer.driverActionTypes.DriverLosesVehicle));
    }
}
